import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const from = params.get("next") || loc.state?.from || "/studio";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      nav(from, { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4"
      style={{ background: "#f7f5f0" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md bg-white rounded-3xl shadow-sm border border-stone-200/80 p-8">
        <div className="flex items-baseline justify-between mb-1">
          <h1 className="font-display text-4xl text-stone-900">Atelier Studio</h1>
          <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500">
            Sign in
          </span>
        </div>
        <p className="text-sm text-stone-500 mb-6">
          Welcome back. Sign in to continue your projects.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Email
            </label>
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              placeholder="you@studio.com"
              className="h-11"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Password
            </label>
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              placeholder="••••••••"
              className="h-11"
            />
          </div>

          {err && (
            <div
              className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
              data-testid="login-error"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="w-full h-11 rounded-full bg-stone-900 text-white hover:bg-stone-800"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                Signing in…
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>

        <p className="text-xs text-stone-500 text-center mt-6">
          New to Atelier Studio?{" "}
          <Link
            to="/register"
            className="text-stone-900 underline underline-offset-2"
            data-testid="login-to-register-link"
          >
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
